import { useEffect, useMemo } from "react";
import Cookies from "js-cookie";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Column } from "react-table";

import { useGetEmailNotifications } from "api/queries/user/useGetEmailNotifications";
import Badge from "components/v2/Badge/Badge";
import usePermissions from "hooks/usePermissions";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import EmailNotificationList from "./EmailNotificationList";
import {
  EMAIL_NOTIFICATION_CONDITION_COLORS,
  EmailNotification,
  formatEmailNotificationCondition,
  formatEmailNotificationEvent,
  formatEmailNotificationScope,
} from "./emailNotificationListDisplay";

function canOpenEmailNotifications(adminSettings?: { view_email_notifications?: boolean }) {
  return Boolean(adminSettings?.view_email_notifications);
}

function EmailNotifications() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;
  const canView = canOpenEmailNotifications(adminSettings);

  const columns = useMemo<Column<EmailNotification>[]>(
    () => [
      {
        id: "name",
        Header: () => <FormattedMessage id="settings.emailNotifications.ruleName" defaultMessage="Rule name" />,
        accessor: "name",
        width: "100%",
        Cell: ({ value }: { value?: string }) => <span>{value?.trim() ? value : "-"}</span>,
      },
      {
        id: "when",
        Header: () => <FormattedMessage id="settings.emailNotifications.when" defaultMessage="When" />,
        accessor: "event",
        width: "100%",
        Cell: ({ value }: { value?: string }) => <span>{value ? formatEmailNotificationEvent(intl, value) : "-"}</span>,
      },
      {
        id: "scope",
        Header: () => <FormattedMessage id="settings.emailNotifications.scope" defaultMessage="Scope" />,
        accessor: "scope",
        width: "100%",
        Cell: ({ row, value }: { row: { original: EmailNotification }; value?: EmailNotification["scope"] }) => (
          <span>{value ? formatEmailNotificationScope(intl, value, row.original.payment_locations ?? []) : "-"}</span>
        ),
      },
      {
        id: "conditions",
        Header: () => <FormattedMessage id="settings.emailNotifications.conditions" defaultMessage="Conditions" />,
        accessor: "conditions",
        width: 260,
        Cell: ({ value }: { value?: EmailNotification["conditions"] }) => {
          if (!value?.length) return <span>-</span>;
          return (
            <div className="d-flex flex-wrap gap-xs">
              {value.map((condition) => (
                <Badge
                  key={condition}
                  type="category-badge"
                  color={EMAIL_NOTIFICATION_CONDITION_COLORS[condition] || "gray"}
                  label={formatEmailNotificationCondition(intl, condition)}
                />
              ))}
            </div>
          );
        },
      },
      {
        id: "status",
        Header: () => <FormattedMessage id="settings.emailNotifications.status" defaultMessage="Status" />,
        accessor: "is_active",
        width: 114,
        align: "center",
        Cell: ({ value }: { value?: boolean }) => (
          <Badge
            type="status-badge"
            color={value ? "success" : "gray"}
            label={
              value
                ? intl.formatMessage({ id: "general.active", defaultMessage: "Active" })
                : intl.formatMessage({ id: "general.inActive", defaultMessage: "Inactive" })
            }
          />
        ),
      },
    ],
    [intl],
  );

  const { instance, fetching, totalPages, totalData, currentPage, itemsPerPage, setItemsPerPage, setCurrentPage } =
    useTableQueryRequest(useGetEmailNotifications, columns, {
      useDefaultSorting: false,
    });

  const handleItemsPerPage = (size: number) => {
    Cookies.set("listing_sizeperpage", String(size));
    setItemsPerPage(size);
  };

  useEffect(() => {
    if (!canView) navigate("/settings/your-details");
  }, [canView, navigate]);

  if (!canView) return null;

  return (
    <>
      <h3 className="mt-3xl">
        <FormattedMessage id="settings.emailNotifications.title" defaultMessage="Email notifications" />
      </h3>
      <div className="mt-xs text-tertiary text-md font-regular mb-4xl">
        <FormattedMessage
          id="settings.emailNotifications.subtitle"
          defaultMessage="Set up email notification for specific shipments"
        />
      </div>
      <EmailNotificationList
        fetching={fetching}
        tableInstance={instance}
        totalPages={totalPages}
        totalData={totalData}
        currentPage={currentPage || 1}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPage}
        onPageClick={setCurrentPage}
      />
    </>
  );
}

export default EmailNotifications;
